/**
 * Who has seen one announcement, and who has not.
 *
 * The count on the list screen says how many. It cannot say whether the
 * announcement worked, because it treats two very different acts as the
 * same one: following the call to action and reading the thing, versus
 * hitting × to make it go away.
 *
 * `via` separates them, and that split is the feedback loop on the
 * announcement itself. A high dismissed share means the copy or the
 * timing is wrong, and there is no way to see that from a total.
 *
 * "Not yet seen" is the half you can act on — it is the list you chase
 * when something actually matters. It leads for that reason.
 */

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useAnnouncementReaders,
  type AdminAnnouncement,
  type AnnouncementReader,
} from '../../api/announcements';
import { colors, docLeading, uiType } from '../../theme';

const GOLD = colors.goldDark;

/**
 * What each `via` value means in plain words.
 *
 * Deliberately not "clicked the button" / "clicked x" — an admin cares
 * what the person did with the news, not which pixel they hit.
 */
interface ViaMeta {
  label: string;
  note: string;
  color: string;
}

const VIA: Record<string, ViaMeta> = {
  opened: {
    label: 'Read It',
    note: 'Followed the button through to the page',
    color: colors.success,
  },
  acknowledged: {
    label: 'Acknowledged',
    note: 'Pressed “Got it”',
    color: GOLD,
  },
  dismissed: {
    label: 'Closed It',
    note: 'Closed without following the button',
    color: colors.textMuted,
  },
};

/** Anything unrecognised — including rows written before `via` existed. */
const UNKNOWN_VIA: ViaMeta = {
  label: 'Seen',
  note: 'Recorded before we tracked how it was closed',
  color: colors.textMuted,
};

function viaMeta(key: string | null | undefined): ViaMeta {
  return (key && VIA[key]) || UNKNOWN_VIA;
}

/** `2026-09-02T14:31:00Z` → `2 Sep, 14:31` in the viewer's timezone. */
function when(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString(
    undefined,
    { hour: '2-digit', minute: '2-digit' },
  )}`;
}

function PersonRow({ reader }: { reader: AnnouncementReader }) {
  const via = reader.acknowledged_at ? viaMeta(reader.via) : null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        borderTop: `1px solid ${colors.borderLight}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: uiType.body, fontWeight: 600, color: colors.textPrimary }}>
          {reader.name}
        </Typography>
        <Typography
          sx={{
            fontSize: uiType.caption,
            color: colors.textMuted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {reader.email}
        </Typography>
      </Box>
      {via && reader.acknowledged_at && (
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Tooltip title={via.note} arrow placement="left">
            <Typography sx={{ fontSize: uiType.caption, fontWeight: 700, color: via.color }}>
              {via.label}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: uiType.micro, color: colors.textMuted }}>
            {when(reader.acknowledged_at)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function SectionHeading({ text, count }: { text: string; count: number }) {
  return (
    <Stack direction="row" gap={1} sx={{ alignItems: 'baseline', mt: 3, mb: 0.5 }}>
      <Typography
        sx={{
          fontSize: uiType.caption,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        {text}
      </Typography>
      <Typography sx={{ fontSize: uiType.caption, fontWeight: 700, color: colors.textPrimary }}>
        {count}
      </Typography>
    </Stack>
  );
}

export function AnnouncementReadersDrawer({
  announcement,
  onClose,
}: {
  announcement: AdminAnnouncement | null;
  onClose: () => void;
}) {
  const query = useAnnouncementReaders(announcement?.id ?? null);
  const data = query.data;

  return (
    <Drawer
      anchor="right"
      open={!!announcement}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 3 } }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: uiType.caption,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: GOLD,
            }}
          >
            Who Has Seen It
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: uiType.title,
              fontWeight: 600,
              color: colors.textPrimary,
              lineHeight: docLeading.tight,
              mt: 0.5,
            }}
          >
            {announcement?.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      {query.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(query.error as Error).message}
        </Alert>
      )}

      {query.isPending && (
        <Stack gap={1} sx={{ mt: 3 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={44} />
          ))}
        </Stack>
      )}

      {data && (
        <>
          {/* The split, before the names. Whether people READ it is the
              question; the roster is how you follow up. */}
          <Stack direction="row" gap={1} sx={{ mt: 2.5, flexWrap: 'wrap' }}>
            {Object.entries(data.by_via)
              .sort((a, b) => b[1] - a[1])
              .map(([key, count]) => {
                const via = viaMeta(key);
                return (
                  <Tooltip key={key} title={via.note} arrow>
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: `1px solid ${via.color}3d`,
                        bgcolor: `${via.color}0f`,
                        minWidth: 78,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: uiType.subtitle, fontWeight: 700, color: via.color }}
                      >
                        {count}
                      </Typography>
                      <Typography
                        sx={{ fontSize: uiType.micro, fontWeight: 600, color: colors.textMuted }}
                      >
                        {via.label}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
          </Stack>

          {data.seen.length === 0 && data.pending.length === 0 && (
            <Typography sx={{ fontSize: uiType.body, color: colors.textMuted, mt: 3 }}>
              No active users to show this to yet.
            </Typography>
          )}

          {data.pending.length > 0 && (
            <>
              <SectionHeading text="Not Yet Seen" count={data.pending.length} />
              <Typography sx={{ fontSize: uiType.caption, color: colors.textMuted, mb: 0.5 }}>
                They will see it next time they sign in.
              </Typography>
              {data.pending.map((r) => (
                <PersonRow key={r.user_id} reader={r} />
              ))}
            </>
          )}

          {data.seen.length > 0 && (
            <>
              <SectionHeading text="Seen" count={data.seen.length} />
              {data.seen.map((r) => (
                <PersonRow key={r.user_id} reader={r} />
              ))}
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
